import {
  DEFAULT_CHUNK_SIZE,
  MAX_OBJECT_BYTES,
  ProtocolError,
  bytesToHex,
  concatBytes,
  equalBytes,
  hexToBytes,
  randomIdentifier,
  type ChunkManifestEntry,
  type ObjectDescriptor,
  type ObjectManifest,
} from '@spectrabus/protocol';
import {
  acceptSenderHandshake,
  completeSenderHandshake,
  createDiscoveryCommitment,
  createSenderHandshake,
} from './session-handshake';

export {
  MAX_AUTHENTICATED_FRAME_PAYLOAD_BYTES,
  PHYSICAL_FRAME_AUTH_TAG_BYTES,
  PhysicalFrameAuthError,
  derivePhysicalFrameAuthKey,
  openPhysicalFrameEnvelope,
  sealPhysicalFrameEnvelope,
  type OpenedPhysicalFrame,
  type PhysicalFrameAuthFailure,
  type PhysicalFrameBinding,
  type PhysicalFrameExpectation,
} from './frame-auth';

export {
  SessionHandshakeError,
  acceptSenderHandshake,
  completeSenderHandshake,
  createDiscoveryCommitment,
  createKeyConfirmation,
  createSenderHandshake,
  verifyKeyConfirmation,
  type KeyConfirmationExpectation,
  type DiscoveryObjectSummary,
  type ReceiverHandshakeParameters,
  type ReceiverHandshakeResult,
  type SenderHandshakeParameters,
  type SenderHandshakeState,
  type SenderOfferExpectation,
  type SessionHandshakeFailure,
  type SplitPeerSession,
} from './session-handshake';

export interface SealedChunk {
  readonly index: number;
  readonly plaintextLength: number;
  readonly ciphertext: Uint8Array;
  readonly ciphertextHash: string;
}

export interface SealedObject {
  readonly descriptor: ObjectDescriptor;
  readonly objectId: string;
  readonly rootKey: Uint8Array;
  readonly chunks: readonly SealedChunk[];
  readonly manifest: ObjectManifest;
  readonly manifestBytes: Uint8Array;
  readonly manifestCommitment: string;
}

export interface SealedOpenExpectation {
  readonly discoveryCommitment: string;
}

export interface EphemeralSession {
  readonly curve: 'P-256';
  readonly senderPublicKey: Uint8Array;
  readonly receiverPublicKey: Uint8Array;
  readonly transcriptHash: string;
  readonly keyFingerprint: string;
  readonly objectRootKey: Uint8Array;
}

export type VerificationStage =
  | 'manifest-commitment'
  | 'chunk-hashes'
  | 'merkle-root'
  | 'aead-tags'
  | 'object-hash';

export class IntegrityError extends ProtocolError {
  public readonly stage: VerificationStage;

  public constructor(stage: VerificationStage, message: string) {
    super('INTEGRITY_FAILURE', message);
    this.name = 'IntegrityError';
    this.stage = stage;
  }
}

function subtle(): SubtleCrypto {
  if (globalThis.crypto?.subtle === undefined) {
    throw new ProtocolError('WEB_CRYPTO_UNAVAILABLE', 'Web Crypto is required for SpectraBus.');
  }
  return globalThis.crypto.subtle;
}

function ownedBuffer(bytes: Uint8Array): ArrayBuffer {
  const owned = new Uint8Array(bytes.length);
  owned.set(bytes);
  return owned.buffer;
}

export async function hashBytes(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest('SHA-256', ownedBuffer(bytes)));
}

export async function hashHex(bytes: Uint8Array): Promise<string> {
  return bytesToHex(await hashBytes(bytes));
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
}

export function canonicalJson(value: unknown): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(stableValue(value)));
}

export async function establishEphemeralSession(
  sessionId: string,
): Promise<EphemeralSession> {
  const sender = await createSenderHandshake({
    sessionId,
    transferId: randomIdentifier(16),
    discoveryCommitment: randomIdentifier(32),
    offerSequence: 0,
    channels: ['optical', 'acoustic'],
  });
  const receiver = await acceptSenderHandshake(sender.offerBytes, { responseSequence: 0 });
  const senderSession = await completeSenderHandshake(sender, receiver.responseBytes);
  if (!equalBytes(senderSession.objectRootKey, receiver.session.objectRootKey)) {
    throw new ProtocolError('KEY_CONFIRMATION', 'Split-peer ephemeral session keys did not agree.');
  }
  return {
    curve: 'P-256',
    senderPublicKey: senderSession.senderPublicKey,
    receiverPublicKey: senderSession.receiverPublicKey,
    transcriptHash: senderSession.transcriptHash,
    keyFingerprint: senderSession.keyFingerprint,
    objectRootKey: senderSession.objectRootKey,
  };
}

function uint32(value: number): Uint8Array {
  const output = new Uint8Array(4);
  new DataView(output.buffer).setUint32(0, value);
  return output;
}

async function deriveChunkKey(rootKey: Uint8Array, objectId: string): Promise<CryptoKey> {
  const material = await subtle().importKey('raw', ownedBuffer(rootKey), 'HKDF', false, ['deriveKey']);
  return subtle().deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: ownedBuffer(hexToBytes(objectId)),
      info: new TextEncoder().encode('SpectraBus/1/chunks'),
    },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function chunkNonce(index: number): Uint8Array {
  const nonce = new Uint8Array(12);
  new DataView(nonce.buffer).setUint32(8, index);
  return nonce;
}

function chunkAad(objectId: string, index: number, plaintextLength: number): Uint8Array {
  return concatBytes(
    new TextEncoder().encode('SpectraBus/1/chunk-aad'),
    hexToBytes(objectId),
    uint32(index),
    uint32(plaintextLength),
  );
}

function largestPowerOfTwoLessThan(value: number): number {
  let power = 1;
  while (power << 1 < value) power <<= 1;
  return power;
}

async function merkleSubtree(leaves: readonly Uint8Array[]): Promise<Uint8Array> {
  if (leaves.length === 1) return leaves[0]?.slice() ?? new Uint8Array();
  const split = largestPowerOfTwoLessThan(leaves.length);
  const left = await merkleSubtree(leaves.slice(0, split));
  const right = await merkleSubtree(leaves.slice(split));
  return hashBytes(concatBytes(Uint8Array.of(1), left, right));
}

export async function merkleRoot(chunks: readonly SealedChunk[]): Promise<string> {
  if (chunks.length === 0) {
    return hashHex(new Uint8Array());
  }
  const leaves = await Promise.all(
    chunks.map((chunk) =>
      hashBytes(
        concatBytes(
          Uint8Array.of(0),
          uint32(chunk.index),
          uint32(chunk.ciphertext.length),
          chunk.ciphertext,
        ),
      ),
    ),
  );
  return bytesToHex(await merkleSubtree(leaves));
}

export async function sealObject(
  bytes: Uint8Array,
  descriptor: Omit<ObjectDescriptor, 'byteLength'>,
  chunkSize = DEFAULT_CHUNK_SIZE,
  suppliedRootKey?: Uint8Array,
): Promise<SealedObject> {
  if (bytes.length > MAX_OBJECT_BYTES) {
    throw new ProtocolError('OBJECT_TOO_LARGE', 'Object exceeds the browser prototype limit.');
  }
  if (chunkSize < 1024 || chunkSize > 128 * 1024) {
    throw new ProtocolError('CHUNK_SIZE', 'Chunk size is outside the negotiated prototype bounds.');
  }

  const objectId = randomIdentifier(16);
  if (suppliedRootKey !== undefined && suppliedRootKey.length !== 32) {
    throw new ProtocolError('ROOT_KEY_LENGTH', 'The object root key must contain exactly 256 bits.');
  }
  const rootKey = suppliedRootKey?.slice() ?? new Uint8Array(32);
  if (suppliedRootKey === undefined) {
    globalThis.crypto.getRandomValues(rootKey);
  }
  const key = await deriveChunkKey(rootKey, objectId);
  const objectHash = await hashHex(bytes);
  const plaintextChunks =
    bytes.length === 0
      ? [new Uint8Array()]
      : Array.from({ length: Math.ceil(bytes.length / chunkSize) }, (_, index) =>
          bytes.slice(index * chunkSize, Math.min(bytes.length, (index + 1) * chunkSize)),
        );

  const chunks: SealedChunk[] = [];
  for (let index = 0; index < plaintextChunks.length; index += 1) {
    const plaintext = plaintextChunks[index] ?? new Uint8Array();
    const ciphertext = new Uint8Array(
      await subtle().encrypt(
        {
          name: 'AES-GCM',
          iv: ownedBuffer(chunkNonce(index)),
          additionalData: ownedBuffer(chunkAad(objectId, index, plaintext.length)),
          tagLength: 128,
        },
        key,
        ownedBuffer(plaintext),
      ),
    );
    chunks.push({
      index,
      plaintextLength: plaintext.length,
      ciphertext,
      ciphertextHash: await hashHex(ciphertext),
    });
  }

  const root = await merkleRoot(chunks);
  const manifest: ObjectManifest = {
    protocolVersion: '1.0',
    objectId,
    name: descriptor.name,
    mimeType: descriptor.mimeType || 'application/octet-stream',
    byteLength: bytes.length,
    chunkSize,
    chunkCount: chunks.length,
    chunks: chunks.map((chunk) => ({
      index: chunk.index,
      plaintextLength: chunk.plaintextLength,
      ciphertextLength: chunk.ciphertext.length,
      ciphertextHash: chunk.ciphertextHash,
    })),
    objectHash,
    merkleRoot: root,
    compression: 'none',
  };
  const manifestBytes = canonicalJson(manifest);
  const manifestCommitment = await computeManifestCommitment(objectId, manifestBytes);

  return {
    descriptor: { ...descriptor, byteLength: bytes.length },
    objectId,
    rootKey,
    chunks,
    manifest,
    manifestBytes,
    manifestCommitment,
  };
}

export async function openSealedObject(
  sealed: SealedObject,
  reconstructedChunks: readonly Uint8Array[],
  expectation: SealedOpenExpectation,
): Promise<Uint8Array> {
  const manifest = await verifyCommittedManifest(sealed, expectation);
  if (reconstructedChunks.length !== manifest.chunks.length) {
    throw new IntegrityError('chunk-hashes', 'Not every encrypted generation was reconstructed.');
  }

  const verifiedChunks: SealedChunk[] = [];
  for (let index = 0; index < reconstructedChunks.length; index += 1) {
    const ciphertext = reconstructedChunks[index] ?? new Uint8Array();
    const expected = manifest.chunks[index];
    if (
      expected === undefined ||
      ciphertext.length !== expected.ciphertextLength ||
      (await hashHex(ciphertext)) !== expected.ciphertextHash
    ) {
      throw new IntegrityError('chunk-hashes', `Encrypted chunk ${index} failed SHA-256 verification.`);
    }
    verifiedChunks.push({
      index: expected.index,
      plaintextLength: expected.plaintextLength,
      ciphertext,
      ciphertextHash: expected.ciphertextHash,
    });
  }
  if ((await merkleRoot(verifiedChunks)) !== manifest.merkleRoot) {
    throw new IntegrityError('merkle-root', 'Reconstructed Merkle root does not match the manifest.');
  }

  const key = await deriveChunkKey(sealed.rootKey, manifest.objectId);
  const plaintextChunks: Uint8Array[] = [];
  for (const chunk of verifiedChunks) {
    plaintextChunks.push(await decryptVerifiedChunk(manifest.objectId, chunk, key));
  }

  const plaintext = concatBytes(...plaintextChunks);
  if (plaintext.length !== manifest.byteLength) {
    throw new IntegrityError('object-hash', 'Reconstructed object length does not match the manifest.');
  }
  const actualHash = await hashBytes(plaintext);
  const expectedHash = hexToBytes(manifest.objectHash);
  if (!equalBytes(actualHash, expectedHash)) {
    throw new IntegrityError('object-hash', 'Final object SHA-256 does not match the sender.');
  }
  return plaintext;
}

const MAX_COMMITTED_MANIFEST_BYTES = 16 * 1024 * 1024;
const MANIFEST_KEYS = [
  'byteLength',
  'chunkCount',
  'chunkSize',
  'chunks',
  'compression',
  'merkleRoot',
  'mimeType',
  'name',
  'objectHash',
  'objectId',
  'protocolVersion',
] as const;
const CHUNK_MANIFEST_KEYS = [
  'ciphertextHash',
  'ciphertextLength',
  'index',
  'plaintextLength',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): boolean {
  const keys = Object.keys(value).sort();
  return keys.length === expected.length && keys.every((key, index) => key === expected[index]);
}

function manifestFailure(message: string): IntegrityError {
  return new IntegrityError('manifest-commitment', message);
}

function parseCommittedChunk(
  value: unknown,
  index: number,
  expectedPlaintextLength: number,
): ChunkManifestEntry {
  if (!isRecord(value) || !hasExactKeys(value, CHUNK_MANIFEST_KEYS)) {
    throw manifestFailure(`Committed chunk ${index} does not use the canonical schema.`);
  }
  const chunkIndex = value.index;
  const plaintextLength = value.plaintextLength;
  const ciphertextLength = value.ciphertextLength;
  const ciphertextHash = value.ciphertextHash;
  if (
    chunkIndex !== index ||
    !Number.isInteger(plaintextLength) ||
    plaintextLength !== expectedPlaintextLength ||
    !Number.isInteger(ciphertextLength) ||
    ciphertextLength !== expectedPlaintextLength + 16 ||
    typeof ciphertextHash !== 'string' ||
    !/^[\da-f]{64}$/u.test(ciphertextHash)
  ) {
    throw manifestFailure(`Committed chunk ${index} contains inconsistent bounds or hashes.`);
  }
  return { index, plaintextLength, ciphertextLength, ciphertextHash };
}

/**
 * Parses and bounds-checks canonical committed manifest bytes. Exported so a
 * physical receiver can interpret a manifest it received over the air with
 * the same strict schema the open path enforces, instead of growing its own
 * divergent parser. The parse alone confers no trust: every open call still
 * re-verifies commitment and discovery binding.
 */
export function parseCommittedManifest(bytes: Uint8Array): ObjectManifest {
  if (bytes.length < 2 || bytes.length > MAX_COMMITTED_MANIFEST_BYTES) {
    throw manifestFailure('Committed manifest exceeds the receiver allocation limit.');
  }
  let value: unknown;
  try {
    const source = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    value = JSON.parse(source) as unknown;
  } catch {
    throw manifestFailure('Committed manifest is not valid canonical UTF-8 JSON.');
  }
  if (!isRecord(value) || !hasExactKeys(value, MANIFEST_KEYS)) {
    throw manifestFailure('Committed manifest does not use the canonical object schema.');
  }

  const {
    protocolVersion,
    objectId,
    name,
    mimeType,
    byteLength,
    chunkSize,
    chunkCount,
    chunks,
    objectHash,
    merkleRoot: committedMerkleRoot,
    compression,
  } = value;
  const nameLength = typeof name === 'string' ? new TextEncoder().encode(name).length : 0;
  const mimeLength = typeof mimeType === 'string' ? new TextEncoder().encode(mimeType).length : 0;
  if (
    protocolVersion !== '1.0' ||
    typeof objectId !== 'string' ||
    !/^[\da-f]{32}$/u.test(objectId) ||
    typeof name !== 'string' ||
    nameLength < 1 ||
    nameLength > 1024 ||
    typeof mimeType !== 'string' ||
    mimeLength < 1 ||
    mimeLength > 255 ||
    !Number.isSafeInteger(byteLength) ||
    (byteLength as number) < 0 ||
    (byteLength as number) > MAX_OBJECT_BYTES ||
    !Number.isInteger(chunkSize) ||
    (chunkSize as number) < 1024 ||
    (chunkSize as number) > 128 * 1024 ||
    !Number.isInteger(chunkCount) ||
    (chunkCount as number) < 1 ||
    !Array.isArray(chunks) ||
    typeof objectHash !== 'string' ||
    !/^[\da-f]{64}$/u.test(objectHash) ||
    typeof committedMerkleRoot !== 'string' ||
    !/^[\da-f]{64}$/u.test(committedMerkleRoot) ||
    compression !== 'none'
  ) {
    throw manifestFailure('Committed manifest contains invalid identifiers or negotiated bounds.');
  }

  const objectLength = byteLength as number;
  const canonicalChunkSize = chunkSize as number;
  const canonicalChunkCount = chunkCount as number;
  const expectedChunkCount = Math.max(1, Math.ceil(objectLength / canonicalChunkSize));
  if (canonicalChunkCount !== expectedChunkCount || chunks.length !== canonicalChunkCount) {
    throw manifestFailure('Committed manifest chunk count does not match the object bounds.');
  }
  const parsedChunks = chunks.map((chunk, index) => {
    const expectedPlaintextLength = index < canonicalChunkCount - 1
      ? canonicalChunkSize
      : objectLength - canonicalChunkSize * (canonicalChunkCount - 1);
    return parseCommittedChunk(chunk, index, expectedPlaintextLength);
  });
  const manifest: ObjectManifest = {
    protocolVersion: '1.0',
    objectId,
    name,
    mimeType,
    byteLength: objectLength,
    chunkSize: canonicalChunkSize,
    chunkCount: canonicalChunkCount,
    chunks: parsedChunks,
    objectHash,
    merkleRoot: committedMerkleRoot,
    compression: 'none',
  };
  if (!equalBytes(canonicalJson(manifest), bytes)) {
    throw manifestFailure('Committed manifest bytes are not in canonical form.');
  }
  return manifest;
}

/**
 * Computes the canonical commitment over committed manifest bytes. The
 * commitment binds the manifest to its object identifier under a fixed
 * domain separator; sealing and every open path derive it through this one
 * function so the two sides can never drift.
 */
export async function computeManifestCommitment(
  objectId: string,
  manifestBytes: Uint8Array,
): Promise<string> {
  return hashHex(
    concatBytes(
      new TextEncoder().encode('SpectraBus/1/manifest-commitment'),
      hexToBytes(objectId),
      manifestBytes,
    ),
  );
}

async function verifyCommittedManifest(
  sealed: SealedObject,
  expectation: SealedOpenExpectation,
): Promise<ObjectManifest> {
  const manifest = parseCommittedManifest(sealed.manifestBytes);
  const expectedCommitment = await computeManifestCommitment(
    manifest.objectId,
    sealed.manifestBytes,
  );
  if (expectedCommitment !== sealed.manifestCommitment) {
    throw manifestFailure('Sealed manifest commitment does not match its canonical bytes.');
  }
  const actualDiscoveryCommitment = await createDiscoveryCommitment({
    name: manifest.name,
    mimeType: manifest.mimeType,
    byteLength: manifest.byteLength,
    objectHash: manifest.objectHash,
    chunkSize: manifest.chunkSize,
    compression: manifest.compression,
  });
  if (actualDiscoveryCommitment !== expectation.discoveryCommitment) {
    throw manifestFailure('Committed manifest does not match the session discovery commitment.');
  }
  return manifest;
}

async function decryptVerifiedChunk(
  objectId: string,
  chunk: SealedChunk,
  key: CryptoKey,
): Promise<Uint8Array> {
  try {
    const plaintext = new Uint8Array(
      await subtle().decrypt(
        {
          name: 'AES-GCM',
          iv: ownedBuffer(chunkNonce(chunk.index)),
          additionalData: ownedBuffer(
            chunkAad(objectId, chunk.index, chunk.plaintextLength),
          ),
          tagLength: 128,
        },
        key,
        ownedBuffer(chunk.ciphertext),
      ),
    );
    if (plaintext.length !== chunk.plaintextLength) {
      throw new IntegrityError('aead-tags', 'Decrypted chunk length differs from its authenticated metadata.');
    }
    return plaintext;
  } catch (error) {
    if (error instanceof IntegrityError) throw error;
    throw new IntegrityError('aead-tags', `AES-GCM verification failed for chunk ${chunk.index}.`);
  }
}

export async function openSealedChunk(
  sealed: SealedObject,
  chunkIndex: number,
  reconstructedCiphertext: Uint8Array,
  expectation: SealedOpenExpectation,
): Promise<Uint8Array> {
  const manifest = await verifyCommittedManifest(sealed, expectation);
  const expected = manifest.chunks[chunkIndex];
  if (
    expected === undefined ||
    reconstructedCiphertext.length !== expected.ciphertextLength ||
    (await hashHex(reconstructedCiphertext)) !== expected.ciphertextHash
  ) {
    throw new IntegrityError('chunk-hashes', `Encrypted chunk ${chunkIndex} failed SHA-256 verification.`);
  }
  const key = await deriveChunkKey(sealed.rootKey, manifest.objectId);
  return decryptVerifiedChunk(
    manifest.objectId,
    {
      index: expected.index,
      plaintextLength: expected.plaintextLength,
      ciphertext: reconstructedCiphertext,
      ciphertextHash: expected.ciphertextHash,
    },
    key,
  );
}
